import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { TodoList } from '../types/todo';
import { Users, Lock, Share2, X } from 'lucide-react';
import { findUserByEmail } from '../utils/userUtils';
import './ListInfoDialog.css';

interface ListInfoDialogProps {
  list: TodoList;
  onClose: () => void;
}

export const ListInfoDialog: React.FC<ListInfoDialogProps> = ({ list, onClose }) => {
  const { currentUser } = useUser();
  const [ownerName, setOwnerName] = useState<string>('');
  const [sharedUserNames, setSharedUserNames] = useState<string[]>([]);

  // Load user names for display
  useEffect(() => {
    const loadUserNames = async () => {
      // Load owner name
      try {
        const owner = await findUserByEmail(list.createdBy, currentUser);
        setOwnerName(owner?.name || list.createdBy);
      } catch {
        setOwnerName(list.createdBy);
      }

      // Load shared user names
      if (list.sharedWith && list.sharedWith.length > 0) {
        const names: string[] = [];
        for (const email of list.sharedWith) {
          try {
            const user = await findUserByEmail(email, currentUser);
            names.push(user?.name || email);
          } catch {
            names.push(email);
          }
        }
        setSharedUserNames(names);
      }
    };
    
    loadUserNames();
  }, [list, currentUser]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="list-info-dialog-backdrop" onClick={handleBackdropClick}>
      <div className="list-info-dialog">
        <div className="list-info-header">
          <h2 className="list-info-title">List Information</h2>
          <button 
            className="close-button" 
            onClick={onClose}
            aria-label="Close dialog"
          >
            <X size={20} />
          </button>
        </div>

        <div className="list-info-content">
          {/* List Name */}
          <div className="info-section">
            <h3 className="list-info-name">{list.name}</h3>
            {list.description && (
              <p className="list-description">{list.description}</p>
            )}
          </div>

          {/* Color Display */}
          <div className="info-section">
            <label className="info-label">Color</label>
            <div className="color-display">
              <div 
                className="color-circle"
                style={{ backgroundColor: list.color || '#3b82f6' }}
              />
              <span className="color-name">
                {list.color || 'Default Blue'}
              </span>
            </div>
          </div>

          {/* Owner Information */}
          <div className="info-section">
            <label className="info-label">Owner</label>
            <div className="owner-info">
              <Users size={16} />
              <span>{ownerName}</span>
            </div>
          </div>

          {/* Sharing Information */}
          <div className="info-section">
            <label className="info-label">Access</label>
            {list.isShared ? (
              <div className="shared-info">
                <div className="shared-indicator">
                  <Share2 size={16} />
                  <span>Shared List</span>
                </div>
                {sharedUserNames.length > 0 && (
                  <div className="shared-users">
                    <strong>Also has access:</strong>
                    <ul className="user-list">
                      {sharedUserNames.map((name, index) => (
                        <li key={index} className="user-item">
                          {name}
                          {currentUser && list.sharedWith[index] === currentUser.email && (
                            <span className="you-indicator"> (You)</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="private-info">
                <Lock size={16} />
                <span>Private List - Only the owner has access</span>
              </div>
            )}
          </div>
        </div>

        <div className="list-info-actions">
          <button 
            className="btn btn-primary" 
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
